// Confirmed child eligibility for the magic-link signup flow is held in sessionStorage rather
// than the Django session. It survives a refresh and the same-tab social-auth round-trip, and is
// re-verified server-side at submit, so it is never trusted as-is.

const STORAGE_KEY = 'blu_child_invite_eligibility'

// Carries the confirmed eligibility ids. Its presence is what tells the server to render the
// contact step rather than the eligibility step, and its value names the children in the banner —
// the selection itself isn't retained server-side. Birthdates are re-verified at submit regardless.
const CONFIRMED_IDS_PARAM = 'confirmed_ids'

// Set on the "back to eligibility" link so the client does not auto-resume forward — the user
// explicitly wants to edit their eligibility rather than continue.
const EDIT_ELIGIBILITY_PARAM = 'edit_eligibility'

export type StoredChildEligibility = {
  // Obfuscated eligibility id (hash_utils.hash); the raw DB id never appears client-side.
  hashed_id: number
  date_of_birth: string
  sex: string
}

export function isEligibilityConfirmed(
  url: string = window.location.href,
): boolean {
  return !!new URL(url).searchParams.get(CONFIRMED_IDS_PARAM)
}

export function withEligibilityConfirmed(
  url: string = window.location.href,
): string {
  const next = new URL(url)
  const ids = readStoredEligibilities().map((e) => e.hashed_id)
  if (ids.length) next.searchParams.set(CONFIRMED_IDS_PARAM, ids.join(','))
  return next.toString()
}

export function isEditingEligibility(
  url: string = window.location.href,
): boolean {
  return new URL(url).searchParams.get(EDIT_ELIGIBILITY_PARAM) === 'true'
}

export function readStoredEligibilities(): StoredChildEligibility[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

export function writeStoredEligibilities(
  eligibilities: StoredChildEligibility[],
): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(eligibilities))
  } catch {
    // sessionStorage can be unavailable (private mode quota); the flow still works via re-entry.
  }
}

export function clearStoredEligibilities(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}
